import React, { JSX, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import checkedIcon from 'images/svg/checked.svg';
import { Flex } from '@motili/ui';

import Config from 'Config';
import Drawer from 'common/components-v2/modal/Drawer';
import { PreviewContainer } from 'common/components/FilePreview';
import { displayDateTime } from 'common/utils/date-time';
import {
    Photo,
    LhsRiskType,
    APPROVAL_STATUS,
} from 'common/types/documentRequirements';
import {
    MetadataPopover,
    MetaAnchor,
    MetaLink,
} from 'common/components/modal/MetadataModal';
import PhotoMetadataModal from 'common/components/modal/PhotoMetadataModal';
import VideoMetadataModal from 'common/components/modal/VideoMetadataModal';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import { WorkOrderRequirementApprovalReasonAPI } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { KebabMenuButton } from 'common/components/buttons/ActionIconButtons/KebabMenuButton';
import {
    isCompleteVideo,
    openVideoInNewTab,
    VideoMask,
    getStatusDetails,
} from '../DocumentRequirementsPhotos/DocumentRequirementsPhotosUploadedRow/WorkOrderRequirementsPhotosUploadedRow';
import { isVideo } from '../DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import { StatusChip, StyledChip } from './StyledChip';

type Mode = 'contractor' | 'admin';

type MetadataPanelProps = {
    open: boolean;
    onClose: () => void;
    metadata: any;
    photoIntegrity?: any;
    document?: any;
};
type RejectionData = {
    rejectionReasons: { id: string; text: string; additional: string[] }[];
    rejectedComments: string;
};
type Props = {
    open: boolean;
    onClose: () => void;
    mode: Mode;
    title: string;
    photo: Photo;
    renderImage: () => JSX.Element;
    showCheck?: boolean;
    showMetadataLink?: boolean;
    onMetadataClick?: () => void;
    isLHS?: boolean;
    riskType?: LhsRiskType;
    riskReasons?: (string | undefined)[];
    adminActions?: AdminActionHandlers;
    headerRight?: ReactNode;
    metadataPanel?: MetadataPanelProps;
    cardMeta?: CardMetaProps;
    photoIndexLabel: string;
    photoRequirementActions?: ReactNode;
    workOrderRequirementApprovalReasons?: WorkOrderRequirementApprovalReasonAPI[];
    showStats?: boolean;
    videoDuration?: string;
    drawerBusy?: boolean;
};

type CardMetaProps = {
    photoIndexLabel: string;
    statusDisplay: string;
    approvalStatusId: string;

    photoIdentification: 'PASS' | 'FAIL' | 'N/A' | string;
    integrityFail: boolean;
    integrityText: string;

    failReasonText: string;
    showFailureInstancesBtn: boolean;
    integrityDetails?: {
        label: string;
        value?: string;
        showInstancesBtn?: boolean;
    }[];

    showApprovalButtons: boolean;
    showEditApproval: boolean;

    hasApprovalHistory: boolean;
    approvalHistoryNode?: ReactNode;
    expandApprovalHistory: boolean;
    requiresPullAndClean?: boolean;
};

type AdminActionHandlers = {
    onApprove?: () => void;
    onReject?: () => void;
    onAccept?: () => void;
    onEdit?: () => void;

    onViewInstances?: () => void;
    onToggleApprovalHistory?: (expand: boolean) => void;

    busy?: boolean;
    disabled?: boolean;
    videoDuration?: string;
};

export default function PhotoViewDetailsDrawer({
    open,
    onClose,
    mode,
    title,
    photo,
    renderImage,
    showCheck = false,
    showMetadataLink = mode === 'admin',
    onMetadataClick,
    isLHS = false,
    riskType,
    riskReasons,
    adminActions,
    headerRight,
    metadataPanel,
    cardMeta,
    photoIndexLabel,
    photoRequirementActions,
    workOrderRequirementApprovalReasons,
    showStats,
    videoDuration,
    drawerBusy,
}: Props) {
    const isAdmin = mode === 'admin';
    const drawerTitle = (
        <TitleWrap>
            <TitleLeft>
                <TitleText>{title}</TitleText>
                {showCheck && <CheckIcon />}
            </TitleLeft>
        </TitleWrap>
    );

    return (
        <Drawer
            drawerStyle={{
                width: '45%',
                paddingBottom: 0,
            }}
            title={drawerTitle}
            onClose={onClose}
            closeByDocument
            showClose
            open={open}
            headerRight={headerRight}
        >
            <Body>
                {isAdmin ? (
                    <AdminContent
                        photo={photo}
                        renderImage={renderImage}
                        headerRight={headerRight}
                        showMetadataLink={showMetadataLink}
                        onMetadataClick={onMetadataClick}
                        adminActions={adminActions}
                        isLHS={isLHS}
                        riskType={riskType}
                        riskReasons={riskReasons}
                        metadataPanel={metadataPanel}
                        cardMeta={cardMeta}
                        workOrderRequirementApprovalReasons={
                            workOrderRequirementApprovalReasons
                        }
                        showStats={showStats}
                        videoDuration={videoDuration}
                        drawerBusy={drawerBusy}
                    />
                ) : (
                    <ContractorContent
                        photo={photo}
                        renderImage={renderImage}
                        headerRight={headerRight}
                        isLHS={isLHS}
                        riskType={riskType}
                        riskReasons={riskReasons}
                        photoIndexLabel={photoIndexLabel}
                        workOrderRequirementApprovalReasons={
                            workOrderRequirementApprovalReasons
                        }
                        videoDuration={videoDuration}
                        drawerBusy={drawerBusy}
                    />
                )}
                {photoRequirementActions && (
                    <PhotoRequirementActionsRow>
                        {photoRequirementActions}
                    </PhotoRequirementActionsRow>
                )}
            </Body>
        </Drawer>
    );
}

/* ---------------- ADMIN  ---------------- */

function AdminContent({
    photo,
    renderImage,
    showMetadataLink,
    onMetadataClick,
    adminActions,
    isLHS,
    riskType,
    riskReasons,
    metadataPanel,
    headerRight,
    cardMeta,
    workOrderRequirementApprovalReasons,
    showStats,
    videoDuration,
    drawerBusy,
}: {
    photo: Photo;
    renderImage: () => JSX.Element;
    showMetadataLink: boolean;
    onMetadataClick?: () => void;
    adminActions?: AdminActionHandlers;
    isLHS?: boolean;
    headerRight?: React.ReactNode;
    riskType?: LhsRiskType;
    riskReasons?: (string | undefined)[];
    metadataPanel?: MetadataPanelProps;
    cardMeta?: CardMetaProps;
    workOrderRequirementApprovalReasons?: WorkOrderRequirementApprovalReasonAPI[];
    showStats?: boolean;
    videoDuration?: string;
    drawerBusy?: boolean;
}) {
    const tooltipDetails = useMemo(() => {
        return getStatusDetails(
            photo,
            workOrderRequirementApprovalReasons || []
        );
    }, [photo, workOrderRequirementApprovalReasons]);
    const hasRejectionTooltip =
        Boolean(photo?.approvalStatusId) && tooltipDetails.length > 0;

    const isDocument = (p: Photo): boolean => {
        const mime = p?.requirement?.mimeType || '';
        return (
            !mime.startsWith('image/') &&
            !mime.startsWith('video/') &&
            mime.length > 0
        );
    };
    return (
        <div>
            <PreviewFrame>
                {drawerBusy && (
                    <BusyOverlay>
                        <i
                            className='fa fa-spinner fa-spin'
                            style={{ fontSize: 28, color: '#fff' }}
                        />
                    </BusyOverlay>
                )}
                {headerRight ? (
                    <KebabMenuButton>{headerRight}</KebabMenuButton>
                ) : null}
                <FileLink
                    href={isVideo(photo) ? undefined : photo.requirement.url}
                    onClick={() => {
                        if (isCompleteVideo(photo)) {
                            openVideoInNewTab(photo);
                        }
                    }}
                    target='_blank'
                    rel='noreferrer'
                    style={{
                        cursor:
                            !isVideo(photo) || isCompleteVideo(photo)
                                ? 'pointer'
                                : 'default',
                    }}
                >
                    <StyledPreviewContainer>
                        <VideoMask photo={photo} duration={videoDuration} />
                        {renderImage()}
                    </StyledPreviewContainer>
                </FileLink>
            </PreviewFrame>

            <HeaderRow>
                <HeaderLeft>
                    <StyledChip>{cardMeta?.photoIndexLabel}</StyledChip>

                    {cardMeta?.approvalStatusId && (
                        <StatusPillWrap>
                            <StatusChip status={cardMeta.approvalStatusId}>
                                {cardMeta.statusDisplay}
                                <StatusInfoIcon
                                    status={cardMeta.approvalStatusId}
                                />
                            </StatusChip>
                            {hasRejectionTooltip && (
                                <RejectionTooltip>
                                    <TooltipBox>
                                        <StatusBulletList>
                                            {tooltipDetails.map((d, idx) => (
                                                <li key={idx}>
                                                    {d.text}
                                                    {d.additional.length >
                                                        0 && (
                                                        <ul
                                                            style={{
                                                                paddingLeft: 16,
                                                                marginTop: 4,
                                                                listStyleType:
                                                                    'disc',
                                                            }}
                                                        >
                                                            {d.additional.map(
                                                                (a, i) => (
                                                                    <li key={i}>
                                                                        {a}
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </StatusBulletList>
                                    </TooltipBox>
                                </RejectionTooltip>
                            )}
                        </StatusPillWrap>
                    )}
                </HeaderLeft>

                <HeaderRight>
                    <MetaAnchor>
                        {showMetadataLink && onMetadataClick && (
                            <MetaLink type='button' onClick={onMetadataClick}>
                                {isVideo(photo)
                                    ? 'VIDEO'
                                    : isDocument(photo)
                                      ? 'DOCUMENT'
                                      : 'PHOTO'}{' '}
                                METADATA
                            </MetaLink>
                        )}

                        {metadataPanel?.open && (
                            <MetadataPopover role='dialog'>
                                {isVideo(photo) ? (
                                    <VideoMetadataModal
                                        variant='drawer'
                                        metadata={metadataPanel.metadata}
                                        document={metadataPanel.document}
                                        handleCloseOnClick={
                                            metadataPanel.onClose
                                        }
                                    />
                                ) : (
                                    <PhotoMetadataModal
                                        variant='drawer'
                                        title={
                                            isDocument(photo)
                                                ? 'Document Metadata'
                                                : 'Photo Metadata'
                                        }
                                        metadata={metadataPanel.metadata}
                                        photoIntegrity={
                                            metadataPanel.photoIntegrity
                                        }
                                        document={metadataPanel.document}
                                        handleCloseOnClick={
                                            metadataPanel.onClose
                                        }
                                        handleReusedViewOnClick={() => {}}
                                    />
                                )}
                            </MetadataPopover>
                        )}
                    </MetaAnchor>
                </HeaderRight>
            </HeaderRow>

            {cardMeta?.requiresPullAndClean && (
                <PullCleanTag>Requires Pull and Clean</PullCleanTag>
            )}

            <DetailsCard>
                <AdminTwoColLayout>
                    <AdminDetailsStack>
                        <Field>
                            <FieldLabel>Name</FieldLabel>
                            <FieldValue>
                                {photo.requirement.fileName || '-'}
                            </FieldValue>
                        </Field>

                        <Field>
                            <FieldLabel>Description</FieldLabel>
                            <FieldValue style={{ margin: 0 }}>
                                {photo.requirement.description || ''}
                            </FieldValue>
                        </Field>

                        <Field>
                            <FieldLabel>Uploaded by</FieldLabel>
                            <FieldValue>
                                {photo.requirement.uploadedBy || '-'}
                            </FieldValue>
                        </Field>

                        <Field>
                            <FieldLabel>Uploaded on</FieldLabel>
                            <FieldValue>
                                {displayDateTime(photo.created)}
                            </FieldValue>
                        </Field>

                        {isLHS && (
                            <LhsSection>
                                <Field>
                                    <FieldLabel>Type</FieldLabel>
                                    <FieldValue>
                                        {riskType?.display || ''}
                                    </FieldValue>
                                </Field>
                                <Field>
                                    <FieldLabel>Reason</FieldLabel>
                                    <FieldValuePreLine>
                                        {riskReasons
                                            ?.filter(Boolean)
                                            .join('\n') || ''}
                                    </FieldValuePreLine>
                                </Field>
                            </LhsSection>
                        )}
                    </AdminDetailsStack>

                    {cardMeta && showStats !== false && !isVideo(photo) && (
                        <AdminRightCol>
                            {/* Classification */}
                            {photo.photoPrediction?.classification &&
                                (() => {
                                    const pct =
                                        (
                                            photo.photoPrediction
                                                .classification as any
                                        ).classificationScore ?? null;
                                    return (
                                        <ClassificationRow>
                                            <FieldLabel>
                                                Classification
                                                {pct != null && (
                                                    <>
                                                        {' '}
                                                        <span
                                                            style={{
                                                                color:
                                                                    pct < 50
                                                                        ? Config
                                                                              .color
                                                                              .red
                                                                        : Config
                                                                              .color
                                                                              .motiliLightBlue,
                                                            }}
                                                        >
                                                            {pct}% Clean
                                                        </span>
                                                    </>
                                                )}
                                            </FieldLabel>
                                            <CleanDirtyBar>
                                                <span>Dirty</span>
                                                <BarTrack>
                                                    <BarFill
                                                        $pct={pct ?? 0}
                                                        $invalid={
                                                            pct != null &&
                                                            pct < 50
                                                        }
                                                    />
                                                </BarTrack>
                                                <span>Clean</span>
                                            </CleanDirtyBar>
                                        </ClassificationRow>
                                    );
                                })()}

                            {/* Identification */}
                            <div>
                                <FieldLabel>Identification</FieldLabel>
                                <StatValue
                                    pass={
                                        String(cardMeta.photoIdentification) ===
                                        'PASS'
                                    }
                                    invalid={
                                        String(cardMeta.photoIdentification) ===
                                        'FAIL'
                                    }
                                >
                                    {String(cardMeta.photoIdentification) ===
                                    'PASS'
                                        ? 'Pass'
                                        : String(
                                                cardMeta.photoIdentification
                                            ) === 'FAIL'
                                          ? 'Fail'
                                          : 'N/A'}
                                </StatValue>
                            </div>

                            {/* Photo Integrity */}
                            <div>
                                <FieldLabel>Photo Integrity</FieldLabel>
                                <StatValue
                                    invalid={cardMeta.integrityFail}
                                    pass={
                                        !cardMeta.integrityFail &&
                                        cardMeta.integrityText === 'Pass'
                                    }
                                >
                                    {cardMeta.integrityText}
                                </StatValue>
                            </div>

                            {/* Failure reason */}
                            {cardMeta.integrityFail && (
                                <FailureBlock>
                                    <FieldLabel>Failure reason</FieldLabel>

                                    {(cardMeta.integrityDetails ?? []).map(
                                        (item, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    marginTop:
                                                        idx === 0 ? 4 : 8,
                                                }}
                                            >
                                                <FailureValueRow>
                                                    <span>{item.label}</span>
                                                    {item.showInstancesBtn && (
                                                        <InfoDotButton
                                                            onClick={
                                                                adminActions?.onViewInstances
                                                            }
                                                        />
                                                    )}
                                                </FailureValueRow>

                                                {item.value && (
                                                    <FailureDetail>
                                                        {item.value}
                                                    </FailureDetail>
                                                )}
                                            </div>
                                        )
                                    )}

                                    {(!cardMeta.integrityDetails ||
                                        cardMeta.integrityDetails.length ===
                                            0) && (
                                        <FailureValueRow>
                                            {cardMeta.showFailureInstancesBtn
                                                ? 'Reused photo'
                                                : cardMeta.failReasonText}
                                            {cardMeta.showFailureInstancesBtn && (
                                                <InfoDotButton
                                                    type='button'
                                                    aria-label='View instances'
                                                    onClick={
                                                        adminActions?.onViewInstances
                                                    }
                                                >
                                                    i
                                                </InfoDotButton>
                                            )}
                                        </FailureValueRow>
                                    )}
                                </FailureBlock>
                            )}
                        </AdminRightCol>
                    )}
                </AdminTwoColLayout>

                {cardMeta.hasApprovalHistory && cardMeta.approvalHistoryNode ? (
                    <ApprovalHistoryWrap>
                        {cardMeta.approvalHistoryNode}
                    </ApprovalHistoryWrap>
                ) : null}
            </DetailsCard>
            {/* actions */}
            {cardMeta?.showApprovalButtons && (
                <ActionsRow justify='center' gap='12px'>
                    <MotiliButton
                        text='Approve'
                        onClick={adminActions?.onApprove}
                        disabled={adminActions?.disabled}
                        busy={adminActions?.busy}
                        style={{ minWidth: 120 }}
                    />
                    <MotiliButton
                        text='Reject'
                        moStyle='red'
                        onClick={adminActions?.onReject}
                        disabled={adminActions?.disabled}
                        busy={adminActions?.busy}
                        style={{ minWidth: 120 }}
                    />
                    <MotiliButton
                        text='Accept'
                        moStyle='normal'
                        onClick={adminActions?.onAccept}
                        disabled={adminActions?.disabled}
                        busy={adminActions?.busy}
                        style={{ minWidth: 120 }}
                    />
                </ActionsRow>
            )}

            {cardMeta?.showEditApproval && (
                <EditRow>
                    <EditBtn
                        type='button'
                        disabled={adminActions?.disabled}
                        onClick={adminActions?.onEdit}
                        style={{
                            minWidth: 120,
                            width: 120,
                            height: 40,
                            fontSize: 14,
                        }}
                    >
                        Edit
                    </EditBtn>
                </EditRow>
            )}
        </div>
    );
}

/* ---------------- CONTRACTOR ---------------- */

function ContractorContent({
    photo,
    renderImage,
    headerRight,
    isLHS,
    riskType,
    riskReasons,
    photoIndexLabel,
    workOrderRequirementApprovalReasons,
    videoDuration,
    drawerBusy,
}: {
    photo: Photo;
    renderImage: () => JSX.Element;
    isLHS?: boolean;
    riskType?: LhsRiskType;
    riskReasons?: (string | undefined)[];
    photoIndexLabel?: string;
    headerRight?: React.ReactNode;
    workOrderRequirementApprovalReasons?: WorkOrderRequirementApprovalReasonAPI[]; //
    videoDuration?: string;
    drawerBusy?: boolean;
}) {
    const { rejectionReasons, rejectedComments } = useMemo(() => {
        if (photo?.approvalStatusId !== 'REJECT') {
            return { rejectionReasons: [], rejectedComments: '' };
        }
        const approval = photo?.approval || {};
        const reasonIds: string[] = approval?.reasons || [];
        const additionalIds: string[] = approval?.additionalReasons || [];

        const rejectionReasons = (workOrderRequirementApprovalReasons || [])
            .filter(r => reasonIds.includes(r.id))
            .map(r => ({
                id: r.id,
                text: r.id === 'OTHER' ? approval?.reasonOther : r.display,
                additional: (r.additionalReasons || [])
                    .filter(a => additionalIds.includes(a.id))
                    .map(a => a.display),
            }))
            .filter(r => Boolean(r.text));

        return {
            rejectionReasons,
            rejectedComments: approval?.additionalNotes || '',
        };
    }, [photo, workOrderRequirementApprovalReasons]);

    const reasonText = riskReasons?.filter(Boolean).length
        ? riskReasons.filter(Boolean).join('\n')
        : '-';

    const getContractorStatus = (p: Photo) => {
        if (!p?.requirement?.photo || !p?.approvalStatusId) return null;
        return (
            APPROVAL_STATUS[
                p.approvalStatusId as keyof typeof APPROVAL_STATUS
            ] || null
        );
    };
    const hasRejection =
        photo?.approvalStatusId === 'REJECT' && rejectionReasons.length > 0;

    return (
        <>
            <PreviewFrame>
                {drawerBusy && (
                    <BusyOverlay>
                        <i
                            className='fa fa-spinner fa-spin'
                            style={{ fontSize: 28, color: '#fff' }}
                        />
                    </BusyOverlay>
                )}
                {headerRight ? (
                    <KebabMenuButton>{headerRight}</KebabMenuButton>
                ) : null}
                <FileLink
                    href={isVideo(photo) ? undefined : photo.requirement.url}
                    onClick={() => {
                        if (isCompleteVideo(photo)) {
                            openVideoInNewTab(photo);
                        }
                    }}
                    target='_blank'
                    rel='noreferrer'
                    style={{
                        cursor:
                            !isVideo(photo) || isCompleteVideo(photo)
                                ? 'pointer'
                                : 'default',
                    }}
                >
                    <StyledPreviewContainer>
                        <VideoMask photo={photo} duration={videoDuration} />
                        {renderImage()}
                    </StyledPreviewContainer>
                </FileLink>
            </PreviewFrame>

            <TopRow>
                <TopLeft>
                    <ChipAndStatusRow>
                        <StyledChip>{photoIndexLabel}</StyledChip>

                        {getContractorStatus(photo) && (
                            <StatusChip status={photo.approvalStatusId}>
                                {getContractorStatus(photo)?.newUIDisplay}
                            </StatusChip>
                        )}
                    </ChipAndStatusRow>

                    {photo?.requirement?.requiresPullAndClean && (
                        <PullCleanTag>Requires Pull and Clean</PullCleanTag>
                    )}
                </TopLeft>
            </TopRow>
            <TwoColLayout $cols={hasRejection ? 2 : 1}>
                <DetailsStack>
                    {/* Uploaded file name */}
                    <Field>
                        <FieldLabel>Name</FieldLabel>
                        <FieldValue>
                            {photo.requirement.fileName || '-'}
                        </FieldValue>
                    </Field>

                    {/*  description  */}
                    <Field>
                        <FieldLabel>Description</FieldLabel>
                        <FieldValue style={{ margin: 0 }}>
                            {photo.requirement.description || ''}
                        </FieldValue>
                    </Field>

                    <Field>
                        <FieldLabel>Uploaded by</FieldLabel>
                        <FieldValue>
                            {photo.requirement.uploadedBy || '-'}
                        </FieldValue>
                    </Field>

                    <Field>
                        <FieldLabel>Uploaded on</FieldLabel>
                        <FieldValue>
                            {displayDateTime(photo.created)}
                        </FieldValue>
                    </Field>

                    {/* LHS Type/Reason */}
                    {isLHS && (
                        <>
                            <Field>
                                <FieldLabel>Type</FieldLabel>
                                <FieldValue>
                                    {riskType?.display || '-'}
                                </FieldValue>
                            </Field>

                            <Field>
                                <FieldLabel>Reason</FieldLabel>
                                <FieldValuePreLine>
                                    {reasonText}
                                </FieldValuePreLine>
                            </Field>
                        </>
                    )}
                </DetailsStack>
                {photo?.approvalStatusId === 'REJECT' && (
                    <RejectionCol>
                        {rejectionReasons.length > 0 && (
                            <Field>
                                <FieldLabel>Rejected Reason</FieldLabel>
                                <ul
                                    style={{
                                        margin: 0,
                                        paddingLeft: 18,
                                        color: '#222',
                                        listStyleType: 'disc',
                                    }}
                                >
                                    {rejectionReasons.map(r => (
                                        <li key={r.id}>
                                            {r.text}
                                            {r.additional?.length > 0 && (
                                                <ul
                                                    style={{
                                                        marginTop: 4,
                                                        paddingLeft: 18,
                                                        color: '#444',
                                                        listStyleType: 'disc',
                                                    }}
                                                >
                                                    {r.additional.map(a => (
                                                        <li
                                                            key={`${r.id}-${a}`}
                                                        >
                                                            {a}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </Field>
                        )}
                        {rejectedComments && (
                            <Field>
                                <FieldLabel>Rejected Comments</FieldLabel>
                                <FieldValue>{rejectedComments}</FieldValue>
                            </Field>
                        )}
                    </RejectionCol>
                )}
            </TwoColLayout>
        </>
    );
}

/* ---------------- styles ---------------- */

const Body = styled.div`
    background: #fff;
    padding-bottom: 50px;
`;

const TitleWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
`;

const TitleLeft = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const TitleText = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: #333333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
    color: ${Config.color.motiliLightBlue};
`;

const CheckIcon = () => (
    <img src={checkedIcon} alt='checked' style={{ width: 14, height: 14 }} />
);
const StyledPreviewContainer = styled(PreviewContainer)`
    width: 100%;
    height: 280px;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;

    & img,
    & video {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
`;

const PreviewFrame = styled.div`
    position: relative;
    width: 100%;
    height: 100%;

    background: #000;
    overflow: hidden;

    border-radius: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    & img,
    & video,
    & canvas {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const KebabWrap = styled.div`
    position: absolute !important;
    top: 8px !important;
    right: 5px !important;
    z-index: 3 !important;

    width: 30px !important;
    height: 20px !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    background: #fff !important;
    border: 1px solid #e6e6e6 !important;

    border-radius: 0px !important;

    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;

    .dropdown,
    .btn-group,
    #motili-text-button,
    [data-test-id='motili-text-button'] {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .MotiliTextButton_motiliTextButton_0a49e {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 0 !important;
    }

    i.fa.fa-ellipsis-h {
        display: block !important;
        width: 18px !important;
        height: 18px !important;
        line-height: 18px !important;
        font-size: 18px !important;

        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
        position: relative !important;
        top: 0 !important;
        left: 0 !important;
    }

    i.fa.fa-ellipsis-h::before {
        display: block !important;
        width: 18px !important;
        height: 18px !important;
        line-height: 18px !important;

        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
        position: relative !important;
        top: 0 !important;
        left: 0 !important;
    }
`;

const FileLink = styled.a`
    &:hover,
    &:active,
    &:visited,
    &:focus {
        color: ${Config.color.motiliLightBlue};
        text-decoration: none;
    }
`;

const TopRow = styled.div`
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TopLeft = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
`;

const ChipAndStatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PullCleanTag = styled.div`
    margin-top: 4px;
    font-size: 12px;
    font-weight: 700;
    color: ${Config.color.red};
    padding-top: 10px;
`;

const DetailsCard = styled.div``;

const TwoColLayout = styled.div<{ $cols?: 1 | 2 }>`
    display: grid;
    grid-template-columns: ${({ $cols }) => ($cols === 1 ? '1fr' : '1fr 1fr')};
    gap: 24px;
    align-items: start;
    margin-top: 12px;
    min-width: 0;

    & > * {
        min-width: 0;
    }
`;

const RejectionCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 14px;
    font-size: 13px;
    color: #222;
`;

const Field = styled.div``;

const FieldLabel = styled.div<{ $invalid?: boolean }>`
    font-size: 14px;
    font-weight: 700;
    color: ${({ $invalid }) => ($invalid ? Config.color.red : '#000')};
    margin-bottom: 4px;
`;

const FieldValue = styled.div`
    font-size: 16px;
    font-weight: 400;
    color: #333;
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
`;

const FieldValuePreLine = styled(FieldValue)`
    white-space: pre-line;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const DetailsStack = styled.div`
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const PhotoRequirementActionsRow = styled.div`
    margin-top: 26px;
    display: flex;
    justify-content: flex-end;

    button,
    button span,
    a,
    a span {
        font-weight: 600 !important;
        font-size: 14px !important;
    }
`;

const ActionsRow = styled(Flex)`
    margin-top: 26px;
    justify-content: flex-end;
`;

const EditRow = styled.div`
    margin-top: 14px;
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;

const EditBtn = styled.button`
    height: 28px;
    min-width: 120px;
    padding: 0 12px;

    border: 1px solid #d9d9d9;
    background: #fff;
    border-radius: 2px;

    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const StatLabel = styled.div`
    color: #666;
    font-weight: 600;
`;

const StatValue = styled.div<{ invalid?: boolean; pass?: boolean }>`
    font-size: 16px;
    font-weight: 400;
    color: ${({ invalid, pass }) =>
        invalid ? Config.color.red : pass ? Config.color.green : Config.color.darkCharcoal};
`;

const FailureBlock = styled.div`
    margin-top: 8px;
    font-size: 12px;
`;

const FailureLabel = styled.div`
    color: #666;
    font-weight: 600;
`;

const FailureDetail = styled.div`
    margin-top: 2px;
    font-size: 13px;
    font-weight: 400;
    color: #333;
`;

const FailureValueRow = styled.div`
    color: ${Config.color.red};
    font-size: 16px;
    font-weight: 400;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    text-align: left;
`;

const AdminTwoColLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 24px;
    align-items: start;
    margin-top: 14px;
    min-width: 0;

    & > * {
        min-width: 0;
    }
`;

const AdminRightCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 13px;
`;

const InfoDotButton = styled(InfoOutlinedIcon)`
    cursor: pointer;
    font-size: 20px !important;
    color: ${Config.color.motiliLightBlue};
    margin-left: 5px;
`;

const StatusInfoIcon = styled(InfoOutlinedIcon)<{ status?: string }>`
    font-size: 24px !important;
    margin-left: 4px;

    ${({ status }) => {
        const s = (status || '').toUpperCase();

        if (s.includes('APPROVE')) return `color: ${Config.color.green};`;
        if (s.includes('REJECT')) return `color: ${Config.color.red};`;
        if (s.includes('ACCEPT')) return `color: ${Config.color.localGray};`;

        return `color: ${Config.color.localGray};`;
    }}
`;

const RejectionTooltip = styled.div`
    display: none;
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 30;
    min-width: 220px;
    max-width: 300px;
    pointer-events: none;
`;
const StatusBulletList = styled.ul`
    margin: 0;
    padding-left: 18px;
    font-size: 13px;

    li + li {
        margin-top: 6px;
    }
`;

const StatusPillWrap = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;

    &:hover ${RejectionTooltip} {
        display: block;
    }
`;

const TooltipBox = styled.div`
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.13);
    padding: 10px 14px;
    font-size: 12px;
    color: #222;
`;

const AdminDetailsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const ApprovalHistoryWrap = styled.div`
    margin-top: 10px;
    width: 100%;
    font-size: 14px;
`;

const LhsSection = styled.div`
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const StatsGridOneCol = styled.div`
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 12px;
    width: 100%;
`;

const ClassificationRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CleanDirtyBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #666;
`;

const BarTrack = styled.div`
    flex: 1;
    height: 6px;
    border-radius: 999px;
    background: #e0e0e0;
    overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $invalid?: boolean }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $invalid }) =>
        $invalid ? Config.color.red : Config.color.motiliLightBlue};
    border-radius: 999px;
`;

const HeaderRow = styled.div`
    margin-top: 10px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const HeaderLeft = styled.div`
    display: inline-flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
`;

const HeaderRight = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding-top: 2px;
    flex-shrink: 0;
`;
const BusyOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
`;
